import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Briefcase,
  Building2,
  Calendar,
  CalendarCheck,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  AlertCircle,
  Clock,
  FileSearch,
  FileText,
  Hash,
  Import,
  Layers,
  LayoutDashboard,
  Link2,
  LogOut,
  Package,
  PanelLeft,
  PanelLeftClose,
  Receipt,
  RotateCcw,
  Settings,
  Shield,
  ShieldAlert,
  ShoppingCart,
  TrendingUp,
  Truck,
  User,
  UserCircle,
  Users,
  Wrench,
} from "lucide-react";
import { CanAccess, useLogout, useUserMenu } from "ra-core";
import { useState } from "react";
import { Link, useLocation } from "react-router";
import { ImportPage } from "../misc/ImportPage";
import { useConfigurationContext } from "../root/ConfigurationContext";

// --- Menu Component Implementations from former Header.tsx ---
const UsersMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users className="w-4 h-4" /> Users
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User className="w-4 h-4" />
        Profile
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings className="w-4 h-4" /> Settings
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import className="w-4 h-4" /> Import data
      </Link>
    </DropdownMenuItem>
  );
};

interface MenuItem {
  label: string;
  icon: any;
  to: string;
}

interface MenuSection {
  label: string;
  items: MenuItem[];
}

export const Sidebar = () => {
  useConfigurationContext();
  const location = useLocation();
  const logout = useLogout();
  const [isMinimized, setIsMinimized] = useState(false);

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    crm: true,
    sales: true,
    documents: true,
    "design_&_production": false,
    operations: false,
    procurement: false,
    hr: false,
    administration: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const menuSections: MenuSection[] = [
    {
      label: "CRM",
      items: [
        { label: "Leads", icon: UserCircle, to: "/contacts" },
        { label: "Enquiries", icon: ClipboardList, to: "/enquiries" },
        { label: "Opportunities", icon: TrendingUp, to: "/opportunities" },
        { label: "Projects", icon: Briefcase, to: "/projects" },
        { label: "Companies", icon: Building2, to: "/companies" },
      ],
    },
    {
      label: "Sales",
      items: [
        { label: "Global Ledger", icon: TrendingUp, to: "/sales" },
        { label: "Auto Invoice", icon: FileText, to: "/dispatch_to_invoice_queue" },
        { label: "Customer Aging", icon: AlertCircle, to: "/customer_aging_view" },
        { label: "Credit Limit", icon: ShieldAlert, to: "/credit-limits" },
        { label: "PDC Tracking", icon: Clock, to: "/pdc_cheques" },
        { label: "Credit Notes", icon: RotateCcw, to: "/credit_notes" },
        { label: "Receipt Matching", icon: Link2, to: "/payment_receipts" },
      ],
    },
    {
      label: "Documents",
      items: [
        { label: "Quotations", icon: FileText, to: "/quotations" },
        { label: "Sales Orders", icon: FileText, to: "/sales_orders" },
        {
          label: "Purchase Orders",
          icon: ShoppingCart,
          to: "/purchase_orders",
        },
        { label: "Proforma Invoice", icon: Receipt, to: "/proforma_invoices" },
        { label: "Invoices", icon: Receipt, to: "/customer_invoices" },
        { label: "Delivery Challan", icon: Truck, to: "/delivery_challans" },
        { label: "Dispatch", icon: Truck, to: "/dispatch_orders" },
      ],
    },
    {
      label: "Design & Production",
      items: [
        { label: "Design Requests", icon: Wrench, to: "/design_requests" },
        { label: "Bill of Materials", icon: Layers, to: "/boms" },
        { label: "RFQs", icon: FileSearch, to: "/rfqs" },
        { label: "Work Orders", icon: Package, to: "/work_orders" },
      ],
    },
    {
      label: "Operations",
      items: [
        { label: "Inventory", icon: Package, to: "/inventory_items" },
        { label: "HSN / SAC Codes", icon: Hash, to: "/hsn_codes" },
        { label: "Service Tickets", icon: Wrench, to: "/service_tickets" },
      ],
    },
    {
      label: "Procurement",
      items: [
        { label: "Vendors", icon: Users, to: "/vendors" },
        { label: "Goods Receipts", icon: Package, to: "/goods_receipts" },
        { label: "Vendor Bills", icon: Receipt, to: "/vendor_bills" },
      ],
    },
    {
      label: "HR",
      items: [
        { label: "Employees", icon: Users, to: "/employees" },
        { label: "Departments", icon: Building2, to: "/hr_departments" },
        { label: "Designations", icon: Briefcase, to: "/hr_designations" },
        { label: "Attendance", icon: Clock, to: "/hr_attendance" },
        {
          label: "Leave Requests",
          icon: CalendarCheck,
          to: "/hr_leave_requests",
        },
        { label: "Holidays", icon: Calendar, to: "/hr_holidays" },
        { label: "Payroll", icon: Receipt, to: "/hr_payroll_runs" },
      ],
    },
    {
      label: "Administration",
      items: [
        { label: "Users", icon: Users, to: "/users" },
        { label: "Roles & Access", icon: Shield, to: "/user-roles" },
        { label: "Permissions", icon: Shield, to: "/permissions" },
      ],
    },
  ];

  // BYPASS: Show all sections to everyone
  const shouldShowSection = (_sectionLabel: string) => true;

  const isLeadsPage = location.pathname.startsWith("/contacts");
  const isCompaniesPage = location.pathname.startsWith("/companies");
  const isSecondSidebarVisible = isLeadsPage || isCompaniesPage;
  const isItemActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    // Ensure exact match or subpath match, preventing greedy partial prefix matches
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  return (
    <aside
      className={`erp-sidebar-primary hidden md:flex flex-col transition-[width] duration-300 ease-in-out shrink-0 ${isMinimized ? "w-20" : "w-64"} ${
        isSecondSidebarVisible ? "!mr-0 !rounded-r-none" : ""
      }`}
    >
      <div
        className={`p-4 flex transition-all duration-300 ${isMinimized ? "flex-col items-center gap-4" : "items-center justify-between"} shrink-0`}
      >
        <Link
          to="/"
          className={`flex items-center no-underline overflow-hidden shrink-0 ${isMinimized ? "justify-center" : "gap-2"}`}
        >
          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-white text-[#00a68d] font-bold shrink-0 shadow-sm border border-black/5">
            E
          </div>
          {!isMinimized && (
            <h1 className="text-xl font-bold text-white whitespace-nowrap">
              ERP
            </h1>
          )}
        </Link>
        <button
          onClick={() => setIsMinimized(!isMinimized)}
          className="p-1.5 hover:bg-white/10 rounded-md text-white/70 transition-colors shrink-0"
          title={isMinimized ? "Expand Sidebar" : "Minimize Sidebar"}
        >
          {isMinimized ? <PanelLeft size={20} /> : <PanelLeftClose size={20} />}
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto erp-sidebar-nav">
        <Link
          to="/"
          className={`erp-sidebar-item overflow-hidden transition-all duration-300 ${isMinimized ? "justify-center px-0 w-10 h-10 mx-auto" : ""} ${
            location.pathname === "/" ? "erp-sidebar-item-active" : ""
          }`}
          title={isMinimized ? "Dashboard" : undefined}
        >
          <LayoutDashboard size={20} className="shrink-0" />
          {!isMinimized && (
            <span
              className={`whitespace-nowrap transition-all duration-300 ${isMinimized ? "w-0 opacity-0 hidden" : "w-auto opacity-100"}`}
            >
              Dashboard
            </span>
          )}
        </Link>

        <div className="h-px bg-sidebar-border/50 my-2 mx-3" />

        {menuSections
          .filter((section) => shouldShowSection(section.label))
          .map((section) => {
            const sectionKey = section.label.toLowerCase().replace(/\s+/g, "_");
            const isExpanded = expandedSections[sectionKey];
            const SectionIcon = section.items[0].icon;

            return (
              <div key={section.label} className="space-y-1">
                <button
                  onClick={() => toggleSection(sectionKey)}
                  className={`erp-sidebar-section-header ${isMinimized ? "justify-center py-2 hover:bg-white/10 rounded-xl transition-all" : ""}`}
                  title={isMinimized ? section.label : undefined}
                >
                  {isMinimized ? (
                    <SectionIcon
                      size={20}
                      className="opacity-80 group-hover:opacity-100"
                    />
                  ) : (
                    <>
                      <span>{section.label}</span>
                      {isExpanded ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </>
                  )}
                </button>

                {isExpanded && (
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const isActive = isItemActive(item.to);
                      return (
                        <Link
                          key={item.label}
                          to={item.to}
                          className={`erp-sidebar-item overflow-hidden transition-all duration-300 ${isMinimized ? "justify-center px-0 w-10 h-10 mx-auto" : ""} ${
                            isActive ? "erp-sidebar-item-active" : ""
                          }`}
                          title={isMinimized ? item.label : undefined}
                        >
                          <item.icon size={18} className="shrink-0" />
                          {!isMinimized && (
                            <span
                              className={`whitespace-nowrap transition-all duration-300 ${isMinimized ? "w-0 opacity-0 hidden" : "w-auto opacity-100"}`}
                            >
                              {item.label}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

        <div className="h-px bg-sidebar-border/50 my-2 mx-3" />

        <Link
          to="/settings"
          className={`erp-sidebar-item overflow-hidden transition-all duration-300 ${isMinimized ? "justify-center px-0 w-10 h-10 mx-auto" : ""} ${
            isItemActive("/settings") ? "erp-sidebar-item-active" : ""
          }`}
          title={isMinimized ? "Settings" : undefined}
        >
          <Settings size={20} className="shrink-0" />
          {!isMinimized && (
            <span
              className={`whitespace-nowrap transition-all duration-300 ${isMinimized ? "w-0 opacity-0 hidden" : "w-auto opacity-100"}`}
            >
              Settings
            </span>
          )}
        </Link>
      </nav>

      <div
        className={`p-3 border-t border-sidebar-border/50 mt-auto flex flex-col gap-2 ${isMinimized ? "items-center" : ""}`}
      >
        <div
          className={`flex items-center ${isMinimized ? "flex-col gap-2" : "justify-between px-2"}`}
        >
          <div
            className={`flex items-center gap-1 ${isMinimized ? "flex-col" : ""}`}
          >
            <ThemeModeToggle />
            <RefreshButton />
          </div>
          <UserMenu>
            <ProfileMenu />
            <CanAccess resource="sales" action="list">
              <UsersMenu />
            </CanAccess>
            <CanAccess resource="configuration" action="edit">
              <SettingsMenu />
            </CanAccess>
            <ImportFromJsonMenuItem />
          </UserMenu>
        </div>

        <button
          className={`erp-sidebar-item w-full overflow-hidden transition-all duration-300 text-destructive hover:text-destructive hover:bg-destructive/10 ${isMinimized ? "justify-center px-0 w-10 h-10 mx-auto" : ""}`}
          title={isMinimized ? "Logout" : undefined}
          onClick={() => logout()}
        >
          <LogOut size={20} className="shrink-0" />
          {!isMinimized && (
            <span
              className={`whitespace-nowrap transition-all duration-300 ${isMinimized ? "w-0 opacity-0 hidden" : "w-auto opacity-100"}`}
            >
              Logout
            </span>
          )}
        </button>
      </div>
    </aside>
  );
};
